import type { ReactNode } from "react";
import { useState } from "react";
import { Alert } from "../ui/alert";
import { Badge } from "../ui/badge";
import { MiniMusicPlayer } from "../blog/MiniMusicPlayer";

// Custom MDX components for enhanced content
interface CalloutProps {
  emoji?: string;
  children: ReactNode;
}

export function Callout({ emoji = "💡", children }: CalloutProps) {
  return (
    <div className="my-6 flex rounded-lg border border-l-4 border-l-primary bg-primary/5 p-4">
      <span className="mr-3 text-2xl">{emoji}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

interface VideoPlayerProps {
  src: string;
  title?: string;
}

export function VideoPlayer({ src, title }: VideoPlayerProps) {
  return (
    <div className="my-8 overflow-hidden rounded-lg">
      <video
        className="w-full"
        controls
        title={title}
        preload="metadata"
      >
        <source src={src} />
        Your browser does not support the video tag.
      </video>
    </div>
  );
}

interface GameItemCardProps {
  itemId: string;
}

export function GameItemCard({ itemId }: GameItemCardProps) {
  // This will be populated with actual game data later
  return (
    <div className="my-4 rounded-lg border bg-card p-4">
      <div className="text-sm text-muted-foreground">Game Item: {itemId}</div>
    </div>
  );
}

interface SkillTreeViewerProps {
  classId: string;
}

export function SkillTreeViewer({ classId }: SkillTreeViewerProps) {
  // This will be populated with actual skill tree data later
  return (
    <div className="my-8 rounded-lg border bg-card p-6">
      <div className="text-sm text-muted-foreground">Skill Tree: {classId}</div>
    </div>
  );
}

interface CodeBlockProps {
  children?: ReactNode;
  className?: string;
  [key: string]: any;
}

export function CodeBlock({ children, className, ...props }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const code = typeof children === 'string' ? children : '';
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group relative">
      <button
        onClick={handleCopy}
        className="absolute right-2 top-2 z-10 rounded bg-gray-700 px-2 py-1 text-xs text-gray-300 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-gray-600"
      >
        {copied ? "Copied!" : "Copy"}
      </button>
      <code className={className} {...props}>
        {children}
      </code>
    </div>
  );
}

// MDX component mappings
export const MDXComponents = {
  // Headings with anchor links
  h1: ({ children, ...props }: any) => (
    <h1 className="mb-6 text-4xl font-bold tracking-tight" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, id, ...props }: any) => (
    <h2 className="group mb-4 text-3xl font-semibold" id={id} {...props}>
      {children}
      {id && (
        <a
          href={`#${id}`}
          className="ml-2 opacity-0 transition-opacity group-hover:opacity-100"
          aria-label={`Link to ${children}`}
        >
          #
        </a>
      )}
    </h2>
  ),
  h3: ({ children, id, ...props }: any) => (
    <h3 className="group mb-3 text-2xl font-semibold" id={id} {...props}>
      {children}
      {id && (
        <a
          href={`#${id}`}
          className="ml-2 opacity-0 transition-opacity group-hover:opacity-100"
          aria-label={`Link to ${children}`}
        >
          #
        </a>
      )}
    </h3>
  ),
  h4: ({ children, id, ...props }: any) => (
    <h4 className="group mb-3 text-xl font-semibold" id={id} {...props}>
      {children}
      {id && (
        <a
          href={`#${id}`}
          className="ml-1 opacity-0 transition-opacity group-hover:opacity-100"
          aria-label={`Link to ${children}`}
        >
          #
        </a>
      )}
    </h4>
  ),
  h5: ({ children, id, ...props }: any) => (
    <h5 className="mb-2 text-lg font-semibold" id={id} {...props}>
      {children}
    </h5>
  ),
  h6: ({ children, id, ...props }: any) => (
    <h6 className="mb-2 text-base font-semibold" id={id} {...props}>
      {children}
    </h6>
  ),

  // Paragraphs and text
  p: ({ children, ...props }: any) => (
    <p className="mb-4 leading-relaxed" {...props}>
      {children}
    </p>
  ),

  // Lists
  ul: ({ children, ...props }: any) => (
    <ul className="mb-4 ml-6 list-disc space-y-1" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: any) => (
    <ol className="mb-4 ml-6 list-decimal space-y-1" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }: any) => (
    <li className="ml-2" {...props}>
      {children}
    </li>
  ),

  // Links
  a: ({ children, href, ...props }: any) => (
    <a
      className="text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:decoration-primary"
      href={href}
      {...props}
    >
      {children}
    </a>
  ),

  // Blockquote
  blockquote: ({ children, ...props }: any) => (
    <blockquote className="mb-4 border-l-4 border-primary/30 pl-4 italic" {...props}>
      {children}
    </blockquote>
  ),

  // Code
  code: CodeBlock,
  pre: ({ children, ...props }: any) => (
    <pre className="mb-4 overflow-x-auto rounded-lg bg-gray-900 p-4" {...props}>
      {children}
    </pre>
  ),

  // Tables
  table: ({ children, ...props }: any) => (
    <div className="mb-4 overflow-x-auto">
      <table className="w-full border-collapse" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }: any) => (
    <thead className="border-b" {...props}>
      {children}
    </thead>
  ),
  tbody: ({ children, ...props }: any) => <tbody {...props}>{children}</tbody>,
  tr: ({ children, ...props }: any) => (
    <tr className="border-b" {...props}>
      {children}
    </tr>
  ),
  th: ({ children, ...props }: any) => (
    <th className="p-2 text-left font-semibold" {...props}>
      {children}
    </th>
  ),
  td: ({ children, ...props }: any) => (
    <td className="p-2" {...props}>
      {children}
    </td>
  ),

  // Horizontal rule
  hr: (props: any) => <hr className="my-8 border-t" {...props} />,

  // Custom components
  Alert,
  Callout,
  Badge,
  Video: VideoPlayer,
  GameItem: GameItemCard,
  SkillTree: SkillTreeViewer,
  MusicPlayer: MiniMusicPlayer,
};